import { useEffect, useRef, useState } from "react";
import { fetchPrayerTimes, fetchScreenData } from "../../services/api";
import { PrayerTimes } from "../../types/PrayerTypes";
import { AzanSettings, HomeSettings, azanSettingsFor } from "./homeSettings";
import { limitAzan } from "./azanVoices";
import { azanSource } from "./ownAzan";

// A second after each minute starts, so the azan begins on time, not up to 15 s into its minute.
const nextCheckMs = () => 61_000 - (Date.now() % 60_000);
// A tablet that was busy for a moment still plays an alert up to 2 minutes late, never later.
const LATE_MINUTES = 2;
const PLAYED_KEY = "m247-azan-played";

const PRAYERS = [
  { name: "fajr", begin: "BegFajr", jamaat: "JamFajr" },
  { name: "dhuhr", begin: "BegDhuhr", jamaat: "JamDhuhr" },
  { name: "asar", begin: "BegAsar", jamaat: "JamAsar" },
  { name: "maghrib", begin: "BegMaghrib", jamaat: "JamMaghrib" },
  { name: "isha", begin: "BegEsha", jamaat: "JamEsha" },
] as const;

export interface AzanAlert {
  key: string;
  minute: number;
}

const toMinutes = (time?: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

// The clock's rules: at the start time, and N minutes before the jama'ah unless that falls before
// the start (where start and jama'ah coincide, at the jama'ah). Fridays: Jumu'ah 1 only.
export const azanAlertsForDay = (
  day: PrayerTimes,
  azan: AzanSettings,
  friday: boolean,
): AzanAlert[] => {
  const alerts: AzanAlert[] = [];
  for (const prayer of PRAYERS) {
    const jamaat = toMinutes(
      friday && prayer.name === "dhuhr" ? day.Jummah1 || day.JamDhuhr : day[prayer.jamaat],
    );
    // Some masjids publish Maghrib's jama'ah only; it follows sunset closely enough to start on.
    const begin = toMinutes(day[prayer.begin]) ?? (prayer.name === "maghrib" ? jamaat : null);
    if (azan.atStart && begin !== null) alerts.push({ key: `${prayer.name}-start`, minute: begin });
    if (azan.beforeJamaah && jamaat !== null) {
      const before = jamaat - azan.minutesBefore;
      const minute = begin !== null && before < begin ? (begin === jamaat ? jamaat : null) : before;
      if (minute !== null && !alerts.some((a) => a.minute === minute)) {
        alerts.push({ key: `${prayer.name}-jamaat`, minute });
      }
    }
  }
  return alerts;
};

// Date, minutes since midnight and weekday in the masjid's own time zone.
export const nowIn = (timeZone: string) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      weekday: "short",
    })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
    friday: parts.weekday === "Fri",
  };
};

const playedToday = (date: string): string[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(PLAYED_KEY) || "null");
    return saved?.date === date ? saved.keys : [];
  } catch {
    return [];
  }
};

const markPlayed = (date: string, key: string) => {
  try {
    localStorage.setItem(PLAYED_KEY, JSON.stringify({ date, keys: [...playedToday(date), key] }));
  } catch {
    // Storage blocked: the 2-minute window still stops repeats within a session.
  }
};

// Plays the household's azan for the home masjid, from the cached timetable, so it works offline.
// `streaming` is true while the home masjid's broadcast plays (its Masjid 24/7 stream, or a partner's
// player that started itself): it carries its own azan. Anything else open (a partner's player
// opened with a tap, a video) gives way to the azan through `makeWay`.
export const useAzanAlerts = (
  settings: HomeSettings | null,
  streaming: boolean,
  makeWay: () => void,
) => {
  const [playing, setPlaying] = useState(false);
  // An azan the browser refused to start (no tap since the page loaded), offered in the bar for a
  // tap until its time has passed. The app never refuses.
  const [blocked, setBlocked] = useState<{ file: string; volume: number; short: boolean } | null>(null);
  const blockedTimer = useRef(0);
  const audio = useRef<HTMLAudioElement | null>(null);
  const streamingRef = useRef(streaming);
  streamingRef.current = streaming;
  const makeWayRef = useRef(makeWay);
  makeWayRef.current = makeWay;

  const stop = () => {
    audio.current?.pause();
    audio.current = null;
    setPlaying(false);
  };

  const play = (file: string, volume: number, short: boolean) => {
    stop();
    setBlocked(null);
    const sound = new Audio(file);
    sound.volume = volume;
    if (short) limitAzan(sound, volume);
    audio.current = sound;
    sound.onended = () => audio.current === sound && stop();
    sound
      .play()
      .then(() => audio.current === sound && setPlaying(true))
      .catch(() => {
        if (audio.current !== sound) return;
        audio.current = null;
        setBlocked({ file, volume, short });
        window.clearTimeout(blockedTimer.current);
        blockedTimer.current = window.setTimeout(() => setBlocked(null), LATE_MINUTES * 60_000);
      });
  };

  useEffect(() => {
    const home = settings?.favourites[0];
    if (!settings || !home) return;
    const azan = azanSettingsFor(settings);
    if (!azan.atStart && !azan.beforeJamaah) return;

    let timeZone: string | null = null;
    let cancelled = false;
    const check = async () => {
      if (!timeZone) {
        const screen = await fetchScreenData(home.id, "home").catch(() => null);
        timeZone = screen ? screen.timeZone || "Europe/London" : null;
      }
      if (!timeZone || cancelled) return;
      const now = nowIn(timeZone);
      const day = await fetchPrayerTimes(now.date, home.id);
      if (cancelled) return;
      const played = playedToday(now.date);
      const due = azanAlertsForDay(day, azan, now.friday).find(
        (alert) =>
          now.minutes >= alert.minute &&
          now.minutes < alert.minute + LATE_MINUTES &&
          !played.includes(alert.key),
      );
      if (!due) return;
      markPlayed(now.date, due.key);
      if (streamingRef.current) return;
      const file = await azanSource(azan.voice, due.key.startsWith("fajr"));
      if (cancelled) return;
      makeWayRef.current();
      play(file, azan.volume, azan.length === "short");
    };

    let timer = 0;
    const tick = () => {
      check();
      timer = window.setTimeout(tick, nextCheckMs());
    };
    tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // play/stop only touch refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(
    () => () => {
      stop();
      window.clearTimeout(blockedTimer.current);
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    playing,
    stop,
    blocked: !!blocked,
    playBlocked: () => blocked && play(blocked.file, blocked.volume, blocked.short),
  };
};
